import React, { ComponentProps, useState } from 'react';
import { useSonTranslation } from '../../translation/useSonTranslation';
import Btn, { BtnModifier } from '../../components/molecules/Btn';
import { Icons } from '../../components/atoms/SVG';
import { TooltipPlacement } from '../../components/atoms/Tooltip';
import { HyperlinkTarget } from '../../components/atoms/Hyperlink';
import { FavoriteButton } from '../../components/forms/FavoriteButton/FavoriteButton';
import { Namespace } from '../../translation/namespace';
import { ButtonMicroInteraction } from '../../components/molecules/Btn/ButtonWrapper';
import ILink from '../../types/ILink';
import { asFetchableBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import ModalDialog from '../../components/organisms/ModalDialog/ModalDialog';
import { SanctionForm } from './partials/SanctionForm';
import { SonForm, TextAreaParam } from '../../_lib/Form2';
import { StatusInfoWarning } from '../../_lib/components/StatusInfo';

export type PersonInteractionProps = {
  userFavorite?: any;
  emailLink?: ILink;
  phoneLink?: ILink;
  conferenceAccessLink?: ILink;
  profileName: string;
  userSanction?: { form?: SonForm<TextAreaParam<'reason'>>; sanctioned: boolean; showSanctionHint?: boolean };
};

export function PersonInteraction({
  emailLink,
  phoneLink,
  profileName,
  conferenceAccessLink,
  userFavorite,
  userSanction,
}: PersonInteractionProps) {
  const { t } = useSonTranslation('PersonInteraction');
  const [sanctionModalOpen, setSanctionModalOpen] = useState(false);

  const closeModal = () => setSanctionModalOpen(false);

  const getEmailButton = () => {
    if (emailLink?.url) {
      return (
        <div className="person-interaction__button-container">
          <div className="contact-person__button">
            <Btn
              href={emailLink.url}
              svgSpriteId={Icons.MAIL_ENVELOPE}
              hiddenText
              titleText={t('email.title', { profileName })}
              tooltipPlacement={TooltipPlacement.TOP}
            >
              {t('email.hiddenText', { profileName })}
            </Btn>
          </div>
        </div>
      );
    }
    return null;
  };

  const getPhoneButton = () => {
    if (phoneLink?.url) {
      return (
        <div className="person-interaction__button-container">
          <div className="contact-person__button">
            <Btn
              href={phoneLink.url}
              svgSpriteId={Icons.PHONE}
              hiddenText
              titleText={t('phone.title', { profileName })}
              tooltipPlacement={TooltipPlacement.TOP}
            >
              {t('phone.hiddenText', { profileName })}
            </Btn>
          </div>
        </div>
      );
    }

    return null;
  };

  const getConferenceButton = () => {
    if (conferenceAccessLink?.url) {
      return (
        <div className="person-interaction__button-container">
          <div className="contact-person__button">
            <Btn
              href={conferenceAccessLink.url}
              svgSpriteId={Icons.VIDEOCALL}
              hiddenText
              titleText={t('conference.title')}
              tooltipPlacement={TooltipPlacement.TOP}
              target={HyperlinkTarget.BLANK}
              routing={false}
            >
              {t('conference.hiddenText', { profileName })}
            </Btn>
          </div>
        </div>
      );
    }
    return null;
  };

  const getFavoriteUser = () => {
    return (
      userFavorite && (
        <FavoriteButton
          id="user-favorite-action-form"
          favorite={userFavorite}
          translationNamespace={Namespace.PersonInteraction}
          favoriteText={t('favorite.buttonText', { profileName })}
          noFavoriteText={t('noFavorite.buttonText', { profileName })}
          svgspriteId={Icons.CONTACTS_PHONEBOOK}
          color={BtnModifier.MINOR_ACTION_ACTIVATED_PETROL}
          resourceToInvalidate={BlockType.favoritedusers}
        />
      )
    );
  };

  const getSanctionButton = () => {
    if (userSanction?.form) {
      const isSanctioned = userSanction.sanctioned;

      return (
        <div className="person-interaction__button-container">
          <ButtonMicroInteraction
            text={
              isSanctioned
                ? t('sanction.sanctioned.button.hiddenText', { profileName })
                : t('sanction.button.hiddenText', { profileName })
            }
            icon={Icons.SANCTION}
            tooltipPlacement={TooltipPlacement.TOP}
            state={isSanctioned ? 'activated' : 'idle'}
            color="red"
            onClick={() => setSanctionModalOpen(true)}
          />
        </div>
      );
    }
    return null;
  };

  const getSanctionModal = () => {
    if (sanctionModalOpen && userSanction?.form) {
      return (
        <ModalDialog
          closeModal={closeModal}
          headline={
            userSanction.sanctioned
              ? t('sanction.sanctioned.modal.headline', { profileName })
              : t('sanction.modal.headline', { profileName })
          }
          initialFocusIndex={1}
        >
          <SanctionForm form={userSanction.form} sanctioned={userSanction.sanctioned} closeModal={closeModal} />
        </ModalDialog>
      );
    }
    return null;
  };

  const getSanctionWarning = () => {
    if (userSanction?.showSanctionHint) {
      return (
        <div className="person-interaction__sanction-warning">
          <StatusInfoWarning message={t('sanction.warning.message')} />
        </div>
      );
    }

    return null;
  };

  if (
    getFavoriteUser() ||
    getEmailButton() ||
    getConferenceButton() ||
    getPhoneButton() ||
    getSanctionButton() ||
    getSanctionWarning()
  ) {
    return (
      <>
        <div className="person-interaction">
          <h2 className="invisible">{t('headline', { name: profileName })}</h2>
          {getSanctionButton()}
          {getFavoriteUser()}
          {getEmailButton()}
          {getPhoneButton()}
          {getConferenceButton()}
          {getSanctionModal()}
        </div>
        {getSanctionWarning()}
      </>
    );
  }

  return null;
}

export const PersonInteractionBlock = asFetchableBlock(
  PersonInteraction,
  BlockType.personinteraction,
  BlockType.personinteraction
);

export type PersonInteractionBlock = ComponentProps<typeof PersonInteractionBlock>;
